import type { StateCreator } from 'zustand';
import { GalleryState } from 'components/common/gallery';
import { DocumentationViewItem } from 'store/documentation/model/types';
import { createBaseGallerySlice } from '../../../gallery/createGallerySlice';
import { GallerySlice, DocumentationDisplayedFile, DocumentationGalleryIds } from './types';
import type { DocumentationStoreSlices } from '../types';
import {
  createPatchedFileWithMetadata,
  findDocumentationForGalleryFile,
  INITIAL_GALLERY_STATE,
  mapRowToDisplayedFile,
  mergeGalleryFilesPreservingSignedUrls,
  resolveGalleryAfterFileRemoval,
  resolveSlideAfterGalleryFilesUpdate,
} from './model';

export * from './types';

type GallerySliceCreator = StateCreator<
  DocumentationStoreSlices,
  [],
  [],
  GallerySlice
>;

export const createGallerySlice: GallerySliceCreator = (set, get) => {
  const baseGallerySlice = createBaseGallerySlice<
    DocumentationGalleryIds,
    DocumentationStoreSlices
  >(set, get);

  return {
    ...baseGallerySlice,
    ...INITIAL_GALLERY_STATE,

    openGalleryAtSlide: (index: number): void => {
      const { currentDirectory } = get();
      set({
        galleryOpen: true,
        currentSlide: index,
        gallerySourceDirectoryId: currentDirectory,
      });
    },

    setGalleryFilesFromGridRows: (rows: DocumentationViewItem[]): void => {
      const { files: previousFiles, galleryOpen, currentSlide } = get();
      const nextFiles = rows
        .map((row) => mapRowToDisplayedFile(row))
        .filter((file): file is DocumentationDisplayedFile => file !== null);

      const files = mergeGalleryFilesPreservingSignedUrls(previousFiles, nextFiles);
      const nextSlide = resolveSlideAfterGalleryFilesUpdate(
        previousFiles,
        files,
        galleryOpen,
        currentSlide
      );

      if (nextSlide === null) {
        // The open file is missing from the new grid listing (e.g. edit/save
        // briefly showed another directory) - keep the previous gallery list
        // instead of adopting an unrelated one, but still patch metadata from
        // the latest docs (directory or search results) when available.
        // Files intentionally removed from the gallery (archive/restore) must
        // go through `removeFileFromGallery`, otherwise they linger here.
        const currentFile = previousFiles[currentSlide];
        const { currentDirectoryDocumentations, searchedDocumentations } = get();
        const doc = currentFile
          ? findDocumentationForGalleryFile(currentFile, [
              currentDirectoryDocumentations,
              searchedDocumentations,
            ])
          : undefined;
        if (currentFile && doc) {
          const patchedFile = createPatchedFileWithMetadata(currentFile, doc);
          if (patchedFile !== currentFile) {
            set({
              files: previousFiles.map((file, index) =>
                index === currentSlide ? patchedFile : file
              ),
            });
          }
        }
        return;
      }

      set(
        nextSlide === currentSlide
          ? { files }
          : { files, currentSlide: nextSlide }
      );
    },

    updateFileAfterUpload: (
      localId: number,
      documentationId: string,
      versionId: string
    ): void => {
      const { files } = get();
      const updatedFiles = files.map((file) => {
        if (file.localId === localId) {
          return {
            ...file,
            documentationId,
            versionId,
            loading: false,
          };
        }
        return file;
      });

      set({ files: updatedFiles });
    },

    selectFiles: (fileIds: number[], options?: { ctrlKeyPressed?: boolean }): void => {
      const { selectedFileIds } = get();
      
      if (options?.ctrlKeyPressed) {
        const newSelection = new Set(selectedFileIds);
        fileIds.forEach((id) => {
          if (newSelection.has(id)) {
            newSelection.delete(id);
          } else {
            newSelection.add(id);
          }
        });
        set({ selectedFileIds: newSelection });
      } else {
        set({ selectedFileIds: new Set(fileIds) });
      }
    },

    unselectFiles: (): void => {
      set({ selectedFileIds: new Set() });
    },

    setGalleryState: (state: GalleryState): void => {
      const { currentDirectory, galleryOpen: wasOpen } = get();
      set({
        galleryOpen: state.open,
        currentSlide: state.slide,
        ...(state.open && !wasOpen
          ? { gallerySourceDirectoryId: currentDirectory }
          : {}),
      });
    },

    removeFileFromGallery: (file, fallbackIndex): void => {
      const next = resolveGalleryAfterFileRemoval(get().files, file, fallbackIndex);
      set({
        files: next.files,
        galleryOpen: next.open,
        currentSlide: next.slide,
      });
    },

    getGalleryState: () => {
      const { galleryOpen, currentSlide } = get();
      return { open: galleryOpen, slide: currentSlide };
    },

    resetGalleryState: (): void => {
      set({
        files: [],
        galleryOpen: false,
        currentSlide: 0,
        selectedFileIds: new Set(),
        gallerySourceDirectoryId: null,
      });
    },
  };
};
